import React, { useMemo, useState } from 'react'
import { Rop03JoinFormPageProps } from 'src/pages/Rop03JoinFormPage'
import styled, { css } from 'styled-components'
import check from 'src/assets/images/rop03/check.svg'
import checkbox from 'src/assets/images/rop03/checkbox.svg'
import nonCheck from 'src/assets/images/rop03/nonCheck.svg'
import nonCheckbox from 'src/assets/images/rop03/nonCheckbox.svg'
import termsChevron from 'src/assets/images/rop03/termsChevron.svg'
import { useFormContext, useWatch } from 'react-hook-form'
import { AllTermsBooleanType, JoinFormType } from 'src/types/react-hook-form'

const Terms = (props: Rop03JoinFormPageProps): JSX.Element => {
  const { registerAttributeExcludeAllTerms: termsData, registerAttribute } = props

  const { getValues, setValue, register, watch } = useFormContext<JoinFormType>()

  // 필수/선택 분리
  const { requiredTerms, optionalTerms } = useMemo(() => {
    const list = termsData ?? []
    return {
      requiredTerms: list.filter((termsItem) => termsItem?.termsTitle?.includes('필수')),
      optionalTerms: list.filter((termsItem) => !termsItem?.termsTitle?.includes('필수')),
    }
  }, [termsData])

  const [openRequired, setOpenRequired] = useState<boolean>(false)
  const [openOptional, setOpenOptional] = useState<boolean>(false)

  const watchedTerms = useWatch({
    name: optionalTerms.map((termsItem) => termsItem.tag as keyof AllTermsBooleanType),
  })

  const panelId = 'required-terms-panel'
  const optionalPanelId = 'optional-terms-panel'

  const allOptionalChecked = useMemo(() => {
    if (!optionalTerms.length) return false
    return optionalTerms.every(
      (termsItem) => !!getValues(termsItem.tag as keyof AllTermsBooleanType),
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optionalTerms, watchedTerms])

  const toggleOptionalAll = (): void => {
    optionalTerms.forEach((termsItem) => {
      setValue(termsItem.tag as keyof AllTermsBooleanType, !allOptionalChecked)
    })
  }

  return (
    <S.TermsAgreeWrap $dim={!!watch('allTerms')}>
      {/* 전체 동의 */}
      <S.AllTermsContainer>
        <HiddenCheckbox
          id="all-terms"
          {...register('allTerms', registerAttribute.allTerms?.register_options)}
          onChange={registerAttribute.allTerms?.register_options?.onClick}
        />
        <S.CheckIcon $checked={!!watch('allTerms')} $variant="all" />
        <p>필수 약관 모두 동의하기</p>
      </S.AllTermsContainer>

      {/* 필수 약관 아코디언 */}
      <S.Accordion>
        <S.EachTermsItem>
          <S.CheckLabel htmlFor="required-all">
            <HiddenCheckbox
              id="required-all"
              onClick={registerAttribute.allTerms?.register_options?.onClick}
            />
            <S.CheckIcon $checked={!!watch('allTerms')} $variant="each" />
          </S.CheckLabel>
          <S.TermsTitleWrap>
            <S.TermsTitle>[필수] 서비스 필수 약관</S.TermsTitle>
            <S.ChevronButton
              type="button"
              aria-controls={panelId}
              aria-expanded={openRequired}
              onClick={() => setOpenRequired((o) => !o)}
            >
              <S.AccordionChevron $open={openRequired} aria-hidden />
            </S.ChevronButton>
          </S.TermsTitleWrap>
        </S.EachTermsItem>
        {openRequired && (
          <S.AccordionPanel id={panelId}>
            {requiredTerms.map((termsItem) => {
              const { tag, termsTitle, register_options, showTermsPopup } = termsItem
              const title = termsTitle?.replace(/\(선택\) |\(필수\) /g, '')
              return (
                <S.EachTermsItem key={tag}>
                  <S.CheckLabel htmlFor={tag}>
                    <HiddenCheckbox
                      id={tag}
                      {...register(tag as keyof AllTermsBooleanType, register_options)}
                    />
                    <S.CheckIcon
                      $checked={!!watch(tag as keyof AllTermsBooleanType)}
                      $variant="each"
                    />
                  </S.CheckLabel>
                  <S.TermsTitleWrap>
                    <S.TermsTitle>{title}</S.TermsTitle>
                    <S.ChevronButton type="button" onClick={showTermsPopup}>
                      <S.Chevron />
                    </S.ChevronButton>
                  </S.TermsTitleWrap>
                </S.EachTermsItem>
              )
            })}
          </S.AccordionPanel>
        )}
      </S.Accordion>
      {optionalTerms.length > 0 && (
        <S.Accordion>
          <S.EachTermsItem>
            {/*✅ 선택 전체 선택 토글*/}
            <S.CheckLabel htmlFor="optional-all">
              <HiddenCheckbox
                id="optional-all"
                checked={allOptionalChecked}
                onChange={toggleOptionalAll}
              />
              <S.CheckIcon $checked={allOptionalChecked} $variant="each" />
            </S.CheckLabel>
            <S.TermsTitleWrap>
              <S.TermsTitle>[선택] 서비스 선택 약관</S.TermsTitle>
              <S.ChevronButton
                type="button"
                aria-controls={optionalPanelId}
                aria-expanded={openOptional}
                onClick={() => setOpenOptional((o) => !o)}
              >
                <S.AccordionChevron $open={openOptional} aria-hidden />
              </S.ChevronButton>
            </S.TermsTitleWrap>
          </S.EachTermsItem>

          {openOptional && (
            <S.AccordionPanel id={optionalPanelId}>
              {optionalTerms.map((termsItem) => {
                const { tag, termsTitle, showTermsPopup } = termsItem
                const title = termsTitle?.replace(/\(선택\) |\(필수\) /g, '')
                const checked = !!getValues(tag as keyof AllTermsBooleanType)
                return (
                  <S.EachTermsItem key={tag}>
                    <S.CheckLabel htmlFor={tag}>
                      <HiddenCheckbox
                        id={tag}
                        checked={checked}
                        onChange={() => setValue(tag as keyof AllTermsBooleanType, !checked)}
                      />
                      <S.CheckIcon $checked={checked} $variant="each" />
                    </S.CheckLabel>

                    <S.TermsTitleWrap>
                      <S.TermsTitle>[선택] {title}</S.TermsTitle>
                      <S.ChevronButton type="button" onClick={showTermsPopup}>
                        <S.Chevron />
                      </S.ChevronButton>
                    </S.TermsTitleWrap>
                  </S.EachTermsItem>
                )
              })}
            </S.AccordionPanel>
          )}
        </S.Accordion>
      )}
    </S.TermsAgreeWrap>
  )
}

export default Terms

const ICONS = {
  all: { on: checkbox, off: nonCheckbox },
  each: { on: check, off: nonCheck },
} as const
type IconVariant = keyof typeof ICONS

const HiddenCheckbox = styled.input.attrs({ type: 'checkbox' })`
  position: absolute !important;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  white-space: nowrap;
  border: 0;
`

const S = {
  TermsAgreeWrap: styled.div<{ $dim?: boolean }>`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    margin-bottom: 30px;
    box-sizing: border-box;
    ${({ $dim }) =>
      $dim &&
      css`
        opacity: 0.3;
        transition: opacity 0.2s ease;
      `}
  `,

  AllTermsContainer: styled.label`
    display: flex;
    align-items: center;
    width: 100%;
    height: 50px;
    padding: 15px 16px;
    gap: 10px;
    flex: 1 0 0;
    cursor: pointer;
    border-radius: 12px;
    background: #fafafa;
    margin-bottom: 12px;
    p {
      color: #666666;
      font-family: ${({ theme }) => theme.fontFamily};
      font-size: 14px;
      font-style: normal;
      font-weight: 500;
      line-height: 24px;
    }
  `,

  EachTermsContainer: styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 4px;
  `,

  EachTermsItem: styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    padding-left: 16px;
    box-sizing: border-box;
  `,

  CheckLabel: styled.label`
    width: 24px;
    height: 24px;
    flex: 0 0 24px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;

    &:has(${HiddenCheckbox}:focus-visible) {
      outline: 2px solid #1a6eff;
      outline-offset: 2px;
      border-radius: 4px;
    }
  `,

  CheckIcon: styled.span<{ $checked: boolean; $variant: IconVariant }>`
    width: 24px;
    height: 24px;
    flex: 0 0 24px;
    background-repeat: no-repeat;
    background-size: contain;
    background-image: ${({ $checked, $variant }) =>
      `url(${$checked ? ICONS[$variant].on : ICONS[$variant].off})`};

    ${HiddenCheckbox}:focus-visible + & {
      outline: 2px solid #1a6eff;
      outline-offset: 2px;
      border-radius: 4px;
    }
  `,
  Accordion: styled.div`
    width: 100%;
  `,
  TermsTitleWrap: styled.div`
    width: 100%;
    display: flex;
    flex-flow: row nowrap;
    align-items: center;
    justify-content: space-between;
  `,
  TermsTitle: styled.p`
    font-size: 10px;
    font-weight: 500;
    color: #717580;
    text-align: left;
  `,

  AccordionChevron: styled.span<{ $open: boolean }>`
    width: 16px;
    height: 16px;
    background: url(${termsChevron}) center / contain no-repeat;
    transform: rotate(${(p) => (p.$open ? '180deg' : '')});
    transition: transform 0.2s ease;
  `,
  Chevron: styled.span`
    width: 16px;
    height: 16px;
    background: url(${termsChevron}) center / contain no-repeat;
    transform: rotate(-90deg);
    transition: transform 0.2s ease;
  `,

  AccordionPanel: styled.div`
    width: 100%;
    padding-left: 16px;
    box-sizing: border-box;
  `,

  ChevronButton: styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border: 0;
    background: transparent;
    cursor: pointer;
  `,
}
